import { useMemo, useState } from 'react'
import { Plus, Clock, Zap } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { useCurrentTick } from '@/hooks/useCurrentTick'
import { useActionPoints } from '@/hooks/useActionPoints'
import { useQueuedActions } from '@/hooks/useQueuedActions'
import { useCancelAction } from '@/hooks/useCancelAction'
import { useCountdown } from '@/hooks/useCountdown'
import { QueuedActionCard } from './QueuedActionCard'
import { ActionTypeSelector } from './ActionTypeSelector'
import { CampaignActionForm } from './actions/CampaignActionForm'
import { FundraiseActionForm } from './actions/FundraiseActionForm'
import { PollActionForm } from './actions/PollActionForm'
import { AttackActionForm } from './actions/AttackActionForm'
import { PRActionForm } from './actions/PRActionForm'
import { InternalActionForm } from './actions/InternalActionForm'
import { OrganizeActionForm } from './actions/OrganizeActionForm'
import { PollingDetailView } from './PollingDetailView'
import type { ActionType, PollingSnapshot } from '@/types'

interface ActionsPanelProps {
  partyId: string
  worldId: string
  tickIntervalHours?: number
  totalTicks?: number
}

export function ActionsPanel({
  partyId,
  worldId,
  tickIntervalHours = 24,
  totalTicks = 16,
}: ActionsPanelProps) {
  const { tickNumber, status: tickStatus, completedAt, isLoading: tickLoading } =
    useCurrentTick(worldId)
  const { total, spent, available, isLoading: apLoading } = useActionPoints(
    partyId,
    tickNumber
  )
  const { actions, isLoading: actionsLoading } = useQueuedActions(
    partyId,
    tickNumber
  )
  const { cancelAction, isCancelling } = useCancelAction(partyId, tickNumber)

  // Modal state for action type selection and configuration
  const [isTypeSelectorOpen, setIsTypeSelectorOpen] = useState(false)
  const [selectedActionType, setSelectedActionType] = useState<ActionType | null>(null)

  // State for displaying poll results immediately after execution
  const [pollResultSnapshot, setPollResultSnapshot] = useState<PollingSnapshot | null>(null)

  // Calculate next tick time
  const nextTickTime = useMemo(() => {
    if (!completedAt) return null
    const lastCompleted = new Date(completedAt)
    return new Date(lastCompleted.getTime() + tickIntervalHours * 60 * 60 * 1000)
  }, [completedAt, tickIntervalHours])

  // Use countdown hook for tick timer
  const countdown = useCountdown(nextTickTime, tickStatus === 'processing')

  const handleCancelAction = (actionId: string) => {
    cancelAction(actionId)
  }

  const handleAddAction = () => {
    setIsTypeSelectorOpen(true)
  }

  const handleSelectActionType = (type: ActionType) => {
    setSelectedActionType(type)
    // Close the type selector when an action is selected
    setIsTypeSelectorOpen(false)
  }

  const handleActionFormClose = () => {
    setSelectedActionType(null)
  }

  const handleBackToTypeSelector = () => {
    setSelectedActionType(null)
    setIsTypeSelectorOpen(true)
  }

  const handlePollSuccess = (snapshot: PollingSnapshot) => {
    // Close the form and open the results view
    setSelectedActionType(null)
    setPollResultSnapshot(snapshot)
  }

  const handleClosePollResults = () => {
    setPollResultSnapshot(null)
  }

  const isLoading = tickLoading || apLoading || actionsLoading

  // Loading state
  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4 animate-pulse">
            <div className="h-4 bg-muted rounded w-1/2" />
            <div className="h-4 bg-muted rounded w-3/4" />
            <div className="h-2 bg-muted rounded" />
            <div className="h-16 bg-muted rounded" />
            <div className="h-16 bg-muted rounded" />
          </div>
        </CardContent>
      </Card>
    )
  }

  const apPercentage = total > 0 ? (available / total) * 100 : 0

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">Actions</CardTitle>
          <span className="text-sm text-muted-foreground">
            Tick {tickNumber} of {totalTicks}
          </span>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Countdown */}
        <div className="flex items-center gap-2 text-sm">
          <Clock className="h-4 w-4 text-muted-foreground" />
          <span className="text-muted-foreground">Next tick in:</span>
          <span className="font-mono font-medium">{countdown}</span>
        </div>

        {/* Action Points */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <Zap className="h-4 w-4 text-amber-500" />
              <span>Action Points</span>
            </div>
            <span className="font-medium">
              {available} / {total} available
            </span>
          </div>
          <Progress value={apPercentage} className="h-2" />
          {spent > 0 && (
            <p className="text-xs text-muted-foreground">
              {spent} AP queued for this tick
            </p>
          )}
        </div>

        {/* Divider */}
        <div className="border-t" />

        {/* Queued Actions */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">
              Queued Actions ({actions.length})
            </span>
          </div>

          {actions.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-sm text-muted-foreground">
                No actions queued for this tick
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {actions.map((action) => (
                <QueuedActionCard
                  key={action.id}
                  action={action}
                  onCancel={handleCancelAction}
                  isCancelling={isCancelling}
                />
              ))}
            </div>
          )}
        </div>

        {/* Add Action Button */}
        <Button
          variant="outline"
          className="w-full"
          onClick={handleAddAction}
          disabled={available <= 0}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Action
        </Button>
        {available <= 0 && (
          <p className="text-xs text-center text-muted-foreground">
            No action points available this tick
          </p>
        )}
      </CardContent>

      {/* Action Type Selector Modal */}
      <ActionTypeSelector
        open={isTypeSelectorOpen}
        onOpenChange={setIsTypeSelectorOpen}
        availableAP={available}
        onSelectType={handleSelectActionType}
      />

      {/* Campaign Action Form */}
      <CampaignActionForm
        open={selectedActionType === 'campaign'}
        onOpenChange={handleActionFormClose}
        partyId={partyId}
        currentTick={tickNumber}
        availableAP={available}
        onBack={handleBackToTypeSelector}
      />

      {/* Fundraise Action Form */}
      <FundraiseActionForm
        open={selectedActionType === 'fundraise'}
        onOpenChange={handleActionFormClose}
        partyId={partyId}
        currentTick={tickNumber}
        availableAP={available}
        onBack={handleBackToTypeSelector}
      />

      {/* Poll Action Form */}
      <PollActionForm
        open={selectedActionType === 'poll'}
        onOpenChange={handleActionFormClose}
        partyId={partyId}
        currentTick={tickNumber}
        availableAP={available}
        onBack={handleBackToTypeSelector}
        onSuccess={handlePollSuccess}
      />

      {/* Attack Action Form */}
      <AttackActionForm
        open={selectedActionType === 'attack'}
        onOpenChange={handleActionFormClose}
        partyId={partyId}
        currentTick={tickNumber}
        availableAP={available}
        onBack={handleBackToTypeSelector}
      />

      {/* PR Action Form */}
      <PRActionForm
        open={selectedActionType === 'pr'}
        onOpenChange={handleActionFormClose}
        partyId={partyId}
        currentTick={tickNumber}
        availableAP={available}
        onBack={handleBackToTypeSelector}
      />

      {/* Internal Action Form */}
      <InternalActionForm
        open={selectedActionType === 'internal'}
        onOpenChange={handleActionFormClose}
        partyId={partyId}
        currentTick={tickNumber}
        availableAP={available}
        onBack={handleBackToTypeSelector}
      />

      {/* Organize Action Form */}
      <OrganizeActionForm
        open={selectedActionType === 'organize'}
        onOpenChange={handleActionFormClose}
        partyId={partyId}
        currentTick={tickNumber}
        availableAP={available}
        onBack={handleBackToTypeSelector}
      />

      {/* Poll Results View - shown immediately after poll execution */}
      <PollingDetailView
        poll={pollResultSnapshot}
        playerPartyId={partyId}
        isOpen={pollResultSnapshot !== null}
        onClose={handleClosePollResults}
      />
    </Card>
  )
}
